import { buttonVariants } from "@arcadeai/design-system/components/ui/atoms/button";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Link } from "@tanstack/react-router";
import { FileText, Receipt, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import { useOrgPermissions } from "@/app/capabilities/api/hooks";
import { ORG } from "@/app/capabilities/permissions";

type TabItem = {
  to: string;
  icon: ReactNode;
  label: string;
  requiresUpdate?: boolean;
};

const TABS: TabItem[] = [
  {
    to: "/overview",
    icon: <Receipt className="size-4" />,
    label: "Overview",
  },
  {
    to: "/invoices",
    icon: <FileText className="size-4" />,
    label: "Invoices",
  },
  {
    to: "/upgrade",
    icon: <TrendingUp className="size-4" />,
    label: "Upgrade",
    requiresUpdate: true,
  },
];

export function BillingTabs() {
  const { canOrg } = useOrgPermissions();
  // Hide the upgrade tab from a caller who may view billing but not change the
  // plan; its route guard 403s `billing_update`, so showing it would dead-end.
  const tabs = TABS.filter(
    (tab) => !tab.requiresUpdate || canOrg(ORG.billing.update)
  );

  return (
    <nav
      aria-label="Billing navigation"
      className="flex w-fit gap-4 rounded-lg bg-muted p-1"
    >
      {tabs.map(({ to, icon, label }) => (
        <Link
          activeProps={{
            className: cn(
              buttonVariants({ variant: "secondary", size: "sm" }),
              "bg-background"
            ),
            "aria-current": "page",
          }}
          className={cn(
            buttonVariants({ variant: "ghost", size: "sm" }),
            "hover:!bg-card flex items-center gap-2"
          )}
          from="/orgs/$organizationId/projects/$projectId/billing"
          key={to}
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          to={`.${to}` as "./overview" | "./invoices" | "./upgrade"}
        >
          {icon}
          <span>{label}</span>
        </Link>
      ))}
    </nav>
  );
}
